<template>
    <div class="appeal-page">
        <!-- 注意事项 -->
        <div class="section notes-section">
            <div class="section-header">
                <span class="section-icon">i</span>
                <span class="section-title">{{ $t('resultsReview.importantNotes') }}</span>
            </div>
            <div class="section-divider"></div>
            <div class="notes-content">
                <p>{{ $t('resultsReview.note1') }}</p>
                <p>{{ $t('resultsReview.note2') }}</p>
                <p>{{ $t('resultsReview.note3') }}</p>
            </div>
        </div>

        <!-- 选择复议项目 -->
        <div class="section module-section">
            <div class="section-title-row">
                <span class="section-title-text">{{ $t('resultsReview.selectModule') }}</span>
            </div>
            <div class="section-divider"></div>
            <div class="module-grid">
                <div
                    v-for="mod in moduleOptions"
                    :key="mod.id"
                    class="module-card"
                    :class="{ active: selectedModule === mod.id }"
                    @click="selectedModule = mod.id"
                >
                    {{ currentLocale === 'zh-CN' ? mod.title : mod.titleEn }}
                </div>
            </div>
        </div>

        <!-- 选择考试订单 -->
        <div class="section order-section">
            <div class="section-title-row">
                <span class="section-title-text">{{ $t('resultsReview.selectOrder') }}</span>
            </div>
            <div class="section-divider"></div>
            <div class="order-list" v-if="orderList.length">
                <div
                    v-for="order in orderList"
                    :key="order.id"
                    class="order-card"
                    :class="{ active: selectedOrder === order.id }"
                    @click="selectedOrder = order.id"
                >
                
                    <div class="order-info">
                        <div class="order-title">
                            {{ currentLocale === 'zh-CN' ? order.examTitle : order.examTitleEn }}
                            <div class="order-radio">
                                <span class="radio-circle" :class="{ checked: selectedOrder === order.id }"></span>
                            </div>
                        </div>
                        <div class="order-divider"></div>
                        <div class="order-detail">
                            <div class="detail-item">
                                <span class="detail-label">{{ $t('resultsReview.testCentre') }}：</span>
                                <span class="detail-value">{{ getExamPointName(order) }}</span>
                            </div>
                            <div class="detail-item">
                                {{ formatExamTime(order) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="empty-tip" v-else>
                {{ currentLocale === 'zh-CN' ? '暂无可复议的考试订单' : 'No test orders are currently eligible for review.' }}
            </div>
        </div>

        <!-- 底部按钮 -->
        <div class="footer-section">
            <el-button
                class="apply-btn"
                :disabled="!canApply"
                @click="handleApply"
            >
                {{ $t('resultsReview.applyNow') }}
            </el-button>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { getReconsiderationModules, getReconsiderationOrders, getReconsiderationOrderId } from "@/plugins/api";
import { toCST, formatChinese, formatEnglishFull } from "@/utils/date";

export default Vue.extend({
    data() {
        return {
            currentLocale: "zh-CN",
            selectedModule: "",
            selectedOrder: null,
            orderList: [],
            moduleOptions: [],
            timer: null
        };
    },
    computed: {
        canApply() {
            return this.selectedModule && this.selectedOrder;
        }
    },
    created() {
        this.currentLocale = this.$i18n.locale || "zh-CN";
        this.getModuleOptions();
    },
    watch: {
        "$i18n.locale": {
            handler(newVal) {
                this.currentLocale = newVal || "zh-CN";
            },
            immediate: true
        },
        selectedModule(newVal) {
            if (newVal) {
                this.selectedOrder = null;
                this.getOrderList();
            } else {
                this.orderList = [];
            }
        }
    },
    methods: {
        async getModuleOptions() {
            const loading = this.$loading({
                lock: true,
                text: this.currentLocale === 'zh-CN' ? '加载中...' : 'Loading...',
                spinner: 'el-icon-loading',
                background: 'rgba(0, 0, 0, 0.7)'
            });
            try {
                const res = await getReconsiderationModules();
                this.moduleOptions = res || [];
            } catch (error) {
                console.error(error);
            } finally {
                loading.close();
            }
        },
        async getOrderList() {
            if (!this.selectedModule) return;
            const loading = this.$loading({
                lock: true,
                text: this.currentLocale === 'zh-CN' ? '加载中...' : 'Loading...',
                spinner: 'el-icon-loading',
                background: 'rgba(0, 0, 0, 0.7)'
            });
            try {
                const res = await getReconsiderationOrders({ examId: this.selectedModule });
                this.orderList = res || [];
            } catch (error) {
                console.error(error);
            } finally {
                loading.close();
            }
        },
        getExamPointName(order) {
            if (!order.examPointName) return "-";
            return this.currentLocale === "zh-CN"
                ? order.examPointName
                : order.examPointNameEn
        },
        formatExamTime(order) {
            if (!order.examTime) return "";
            if (this.currentLocale === "zh-CN") {
                const sessions = order.sessions || "";
                return `${formatChinese(order.examTime)} ${sessions}`;
            } else {
                const sessions = order.sessionsEn || "";
                return `${formatEnglishFull(order.examTime)} ${sessions}`;
            }
        },
        async handleApply() {
            clearTimeout(this.timer)
            this.timer = setTimeout(async ()=>{
                if (!this.canApply) return;
                const loading = this.$loading({
                    lock: true,
                    text: this.currentLocale === 'zh-CN' ? '提交中...' : 'Submitting...',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)'
                });
                try {
                    const res = await getReconsiderationOrderId({examId: this.selectedModule, orderId: this.selectedOrder})
                    if (res) {
                        this.$router.push({
                            path: '/appealReconsiderationPay',
                            query: {
                                orderId: this.selectedOrder,
                                examId: this.selectedModule,
                                type: 1
                            }
                        });
                    }
                } finally {
                    loading.close();
                }
            },500)
        }
    }
});
</script>

<style lang="stylus" scoped>
.appeal-page {
    max-width: 960px;
    margin: 24px auto;
    padding: 0 20px;

    .section {
        margin-bottom: 28px;

        .section-header {
            display: flex;
            align-items: center;
            margin-bottom: 8px;

            .section-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background-color: #011E41;
                color: #fff;
                font-size: 13px;
                font-weight: bold;
                margin-right: 8px;
                font-style: italic;
            }

            .section-title {
                font-size: 16px;
                font-weight: 600;
                color: #011E41;
            }
        }

        .section-title-row {
            margin-bottom: 8px;

            .section-title-text {
                font-size: 16px;
                font-weight: 600;
                color: #011E41;
            }
        }

        .section-divider {
            height: 3px;
            background: linear-gradient(90deg, #E2CB24 0%, #50B9AA 100%);
            margin-bottom: 16px;
        }
    }

    // 注意事项
    .notes-section {
        .notes-content {
            background-color: #fff;
            border-radius: 8px;
            padding: 16px 20px;
            p {
                font-size: 14px;
                line-height: 1.3;
                color: #303133;
                margin-bottom: 8px;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }
    }

    // 复议项目选择
    .module-section {
        .module-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;

            .module-card {
                background-color: #fff;
                border: 1px solid #dcdfe6;
                border-radius: 8px;
                padding: 20px;
                text-align: center;
                font-size: 16px;
                color: #303133;
                cursor: pointer;
                transition: all 0.25s ease;
                user-select: none;

                &:hover {
                    border-color: #0043e0;
                    box-shadow: 0 2px 8px rgba(80, 185, 170, 0.15);
                }

                &.active {
                    border-color: #0043e0;
                    border-width: 2px;
                    background-color: #f0faf8;
                    color: #011E41;
                    font-weight: 600;
                }
            }
        }
    }

    // 考试订单选择
    .order-section {
        .order-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .order-card {
            display: flex;
            align-items: stretch;
            background-color: #fff;
            border: 1px solid #dcdfe6;
            border-radius: 10px;
            padding: 16px 20px;
            cursor: pointer;
            transition: all 0.25s ease;

            &:hover {
                border-color: #0043e0;
            }

            &.active {
                border-color: #0043e0;
                border-width: 2px;
                box-shadow: 0 2px 10px rgba(80, 185, 170, 0.2);
            }

            .order-radio {
                display: flex;
                align-items: center;
                padding-right: 16px;
                position: absolute;
                right: 10px;
                top: 20%;
                .radio-circle {
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    border: 2px solid #c0c4cc;
                    position: relative;
                    transition: all 0.2s ease;

                    &.checked {
                        border-color: #0043e0;

                        &::after {
                            content: '';
                            position: absolute;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                            width: 10px;
                            height: 10px;
                            border-radius: 50%;
                            background-color: #0043e0;
                        }
                    }
                }
            }

            .order-info {
                flex: 1;
                min-width: 0;

                .order-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #011E41;
                    margin-bottom: 8px;
                    position relative
                }

                .order-divider {
                    height: 2px;
                    background: linear-gradient(90deg, #E2CB24 0%, #50B9AA 100%);
                    margin-bottom: 10px;
                }

                .order-detail {
                    .detail-item {
                        font-size: 14px;
                        color: #606266;
                        margin-bottom: 4px;

                        &:last-child {
                            margin-bottom: 0;
                        }

                        .detail-label {
                            color: #606266;
                        }

                        .detail-value {
                            color: #303133;
                        }
                    }
                }
            }
        }

        .empty-tip {
            text-align: center;
            padding: 40px 20px;
            color: #909399;
            font-size: 14px;
        }
    }

    // 底部按钮
    .footer-section {
        display: flex;
        justify-content: center;
        margin-top: 32px;

        .apply-btn {
            min-width: 200px;
            height: 44px;
            background: #0043e0;
            border: none;
            border-radius: 22px;
            color: #fff;
            font-size: 16px;
            font-weight: 600;

            &:hover:not(:disabled) {
                opacity: 0.9;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }
    }
}

@media (max-width: 640px) {
    .appeal-page {
        margin: 16px auto;
        padding: 0 12px;

        .section {
            margin-bottom: 20px;
        }

        .module-section {
            .module-grid {
                grid-template-columns: 1fr;
                gap: 10px;

                .module-card {
                    padding: 16px;
                    font-size: 14px;
                }
            }
        }

        .order-section {
            .order-card {
                padding: 12px 14px;

                .order-radio {
                    padding-right: 10px;

                    .radio-circle {
                        width: 16px;
                        height: 16px;

                        &.checked::after {
                            width: 8px;
                            height: 8px;
                        }
                    }
                }

                .order-info {
                    .order-title {
                        font-size: 15px;
                    }

                    .order-detail {
                        .detail-item {
                            font-size: 13px;
                        }
                    }
                }
            }
        }

        .footer-section {
            .apply-btn {
                min-width: 160px;
                height: 40px;
                font-size: 15px;
            }
        }
    }
}
</style>
